import { FormattedCurrency } from '@shared/utils/currency';
import { Box, Flex, PublicBoxProps } from '@withjoy/joykit';
import React from 'react';
import { useCatalogRegistryTranslations } from '../../../../Catalog.i18n';
import { CatalogProductItemDetails } from '../../../../Catalog.types';
import { StyledButton, StyledContainer, StyledImg, StyledImgContainer, StyledPrice, StyledTitle, styles } from './ProductQuickAddBar.styles';
import { SkeletonGroup, SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { addRendition } from '@shared/utils/photoRendition';
import { AddCatalogItemArgs } from '../BuyForm/BuyForm.types';

interface ProductQuickAddBarProps
  extends Readonly<
    {
      brand?: string;
      name?: string;
      priceFloatingPointDecimalString?: string;
      priceCurrencyCode?: string;
      isAddingItem: boolean;
      photoUrl?: string;
      isMobile?: boolean;
      onAddCatalogItem: (args?: AddCatalogItemArgs) => void;
      storefrontInfo: Maybe<CatalogProductItemDetails['storefrontInfo']>;
      showSkeleton?: boolean;
    } & PublicBoxProps
  > {}

export const ProductQuickAddBar: React.FC<ProductQuickAddBarProps> = props => {
  const { brand, name, photoUrl, priceFloatingPointDecimalString, priceCurrencyCode, isAddingItem, onAddCatalogItem, storefrontInfo, isMobile, showSkeleton, ...restProps } = props;
  const { getAddProductTranslations } = useCatalogRegistryTranslations();
  const { addToRegistry } = getAddProductTranslations();

  const onScreenPhotoUrl = addRendition({ url: photoUrl, renditionSize: 'medium', isProduct: true });
  const hasPrice = priceFloatingPointDecimalString && priceCurrencyCode;

  return (
    <SkeletonGroup
      isReady={!showSkeleton}
      placeholder={<ProductQuickAddBarSkeleton isMobile={isMobile} />}
    >
      <StyledContainer
        __css={styles.container}
        {...restProps}
      >
        {/* Conflict in footer design, removing for now  */}
        {/* {isMobile && photoUrl && <StyledGradient __css={styles.gradient} />} */}
        {!isMobile && photoUrl && (
          <StyledImgContainer __css={styles.imgContainer}>
            <StyledImg photoUrl={onScreenPhotoUrl} />
          </StyledImgContainer>
        )}
        <Box __css={styles.contentContainer}>
          {!isMobile && (
            <Flex
              justifyContent={'space-between'}
              overflow={'hidden'}
            >
              {(brand || name) && (
                <StyledTitle __css={styles.text}>
                  {brand} {name}
                </StyledTitle>
              )}
            </Flex>
          )}

          <Flex alignItems={'center'}>
            {!isMobile && hasPrice && (
              <StyledPrice
                __css={styles.text}
                marginX={5}
              >
                <FormattedCurrency
                  priceFloatingPointDecimalString={priceFloatingPointDecimalString}
                  priceCurrencyCode={priceCurrencyCode}
                  formatForm="short"
                  shouldCalculateCharmPrice={true}
                />
              </StyledPrice>
            )}
            <StyledButton
              shape={isMobile ? 'rounded' : 'square'}
              onClick={() => onAddCatalogItem()}
              loading={isAddingItem}
              disabled={!storefrontInfo?.canAddToRegistry}
              overrides={{
                Root: {
                  ...styles.buttonOverrides.Root,
                  props: {
                    ...styles.buttonOverrides.Root?.props,
                    height: isMobile ? 9 : 8,
                    backgroundColor: 'color(display-p3 0.337255 0 0.95)',
                    backgroundImage:
                      'radial-gradient(circle at var(--mouse-x, 0%) var(--mouse-y, 50%), color(display-p3 0.286275 0.317647 0.937255) 0%, color(display-p3 0.337255 0 0.95) 100%)',
                    backgroundPosition: '0% 50%',

                    _hover: {
                      color: 'white',
                      backgroundSize: '100% 100%',
                    },
                    _active: {
                      color: 'white',
                      transform: 'scale(0.96)',
                      transition: 'transform 0.2s ease-in-out',
                    },
                    _disabled: {
                      backgroundImage: 'none',
                    },
                  },
                },
              }}
            >
              {addToRegistry}
            </StyledButton>
          </Flex>
        </Box>
      </StyledContainer>
    </SkeletonGroup>
  );
};

const ProductQuickAddBarSkeleton: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  return (
    <Box __css={styles.container}>
      <SkeletonThumbnail
        marginRight={{ _: 1, sm: 5 }}
        skeletonSize={'75px'}
      ></SkeletonThumbnail>
      <Box __css={styles.contentContainer}>
        <SkeletonText
          rows={1}
          skeletonHeight={[6, null, 7]}
          width={['100%', null, '350px']}
        ></SkeletonText>
        <Flex alignItems={'center'}>
          {!isMobile && (
            <SkeletonText
              rows={1}
              skeletonHeight={[6, null, 7]}
              width={'60px'}
              marginRight={5}
            ></SkeletonText>
          )}
          <SkeletonThumbnail
            width={['100%', null, '220px']}
            height={'48px'}
          ></SkeletonThumbnail>
        </Flex>
      </Box>
    </Box>
  );
};
